import React from 'react';
import { Brain, Check, X } from 'lucide-react';

import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';

type ReviewProposal = {
  id: string;
  instance_id: string;
  proposal_type: 'memory_fact' | 'skill_hint' | 'operating_edit';
  title: string;
  summary: string;
  payload: Record<string, unknown>;
  evidence_refs: unknown[];
  confidence: number | null;
  created_at: string;
};

type ReviewerStatus = {
  scheduler: {
    last_run_at: string | null;
    last_run_status: string | null;
    last_run_summary: Record<string, unknown>;
  };
  instances: {
    total: number;
    ok: number;
    skipped: number;
    error: number;
    error_instances: Array<{
      instance_id: string;
      last_run_at: string | null;
      error: string | null;
    }>;
  };
};

const TYPE_LABELS: Record<ReviewProposal['proposal_type'], string> = {
  memory_fact: 'Speicher-Fakt',
  skill_hint: 'Skill-Hinweis',
  operating_edit: 'Betriebs-Prompt',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AdminKiReviewTab() {
  const [proposals, setProposals] = React.useState<ReviewProposal[]>([]);
  const [reviewerStatus, setReviewerStatus] = React.useState<ReviewerStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/review-proposals', { credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error || 'Vorschläge konnten nicht geladen werden.');
      }
      const data = await res.json() as { proposals?: ReviewProposal[]; reviewerStatus?: ReviewerStatus };
      setProposals(data.proposals ?? []);
      setReviewerStatus(data.reviewerStatus ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler.');
      setProposals([]);
      setReviewerStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const handleApprove = async (proposalId: string) => {
    setActionLoading(`${proposalId}:approve`);
    setActionError(null);
    try {
      const res = await fetch(`/api/admin/review-proposals/${encodeURIComponent(proposalId)}/approve`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error || 'Annehmen fehlgeschlagen.');
      }
      setProposals((prev) => prev.filter((p) => p.id !== proposalId));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Annehmen fehlgeschlagen.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (proposalId: string) => {
    setActionLoading(`${proposalId}:reject`);
    setActionError(null);
    try {
      const res = await fetch(`/api/admin/review-proposals/${encodeURIComponent(proposalId)}/reject`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error || 'Ablehnen fehlgeschlagen.');
      }
      setProposals((prev) => prev.filter((p) => p.id !== proposalId));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Ablehnen fehlgeschlagen.');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <InlineLoadingState label="Laden…" className="py-10" />
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-red-600">{error}</p>
        <button type="button" onClick={() => void load()} className="mt-3 text-xs text-violet-600 hover:underline">
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {actionError ? (
        <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {actionError}
        </div>
      ) : null}

      <p className="text-xs text-app-text-subtle leading-relaxed">
        Der tägliche KI-Review schlägt neue Firmen-Fakten sowie Skill- und Prompt-Anpassungen vor.
        Manuelle Einträge über das Gehirn-Symbol im Chat werden nicht hier angezeigt.
        Annehmen speichert Fakten; Skills/Prompt werden nur als freigegeben markiert.
      </p>

      {reviewerStatus ? (
        <div className="rounded-lg border border-app-border bg-app-surface-muted px-3 py-2 text-xs text-app-text-muted space-y-1">
          <p>
            <span className="font-medium text-app-text-muted">Letzter Review-Lauf: </span>
            {reviewerStatus.scheduler.last_run_at
              ? formatDate(reviewerStatus.scheduler.last_run_at)
              : 'Noch kein Lauf'}
            {reviewerStatus.scheduler.last_run_status ? (
              <span className="ml-2 text-slate-400">({reviewerStatus.scheduler.last_run_status})</span>
            ) : null}
          </p>
          <p className="text-app-text-subtle">
            {reviewerStatus.instances.ok} erfolgreich · {reviewerStatus.instances.skipped} übersprungen
            {reviewerStatus.instances.error > 0 ? (
              <span className="text-amber-700"> · {reviewerStatus.instances.error} Fehler</span>
            ) : null}
          </p>
          {reviewerStatus.instances.error_instances.length > 0 ? (
            <p className="text-amber-700">
              Fehlgeschlagene Instanzen werden beim nächsten Lauf erneut versucht.
            </p>
          ) : null}
          <p className="text-slate-400">
            Das Datum bei jedem Vorschlag ist das Erstellungsdatum, nicht der letzte Review-Lauf.
          </p>
        </div>
      ) : null}

      {proposals.length === 0 ? (
        <div className="text-center py-10">
          <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-3">
            <Brain className="w-6 h-6 text-violet-600" />
          </div>
          <p className="text-sm font-medium text-app-text-muted">Keine offenen KI-Vorschläge</p>
          <p className="text-xs text-slate-400 mt-1">Der Reviewer hat nichts zur Freigabe eingereicht.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {proposals.map((p) => {
            const busyApprove = actionLoading === `${p.id}:approve`;
            const busyReject = actionLoading === `${p.id}:reject`;
            const busy = busyApprove || busyReject;
            const expanded = expandedId === p.id;
            return (
              <li key={p.id} className="rounded-lg border border-app-border bg-app-surface overflow-hidden">
                <div className="p-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">
                        {TYPE_LABELS[p.proposal_type]}
                      </span>
                      {typeof p.confidence === 'number' ? (
                        <span className="text-[10px] text-slate-400">
                          Confidence {(p.confidence * 100).toFixed(0)}%
                        </span>
                      ) : null}
                      <span className="text-[10px] text-slate-400" title="Erstellungsdatum des Vorschlags">
                        Vorschlag vom {formatDate(p.created_at)}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-app-text">{p.title}</p>
                    <p className="text-xs text-app-text-muted mt-1">
                      {p.proposal_type === 'memory_fact' && typeof p.payload.key === 'string' ? (
                        <>
                          <span className="text-slate-400">{p.payload.key}: </span>
                          {p.summary}
                        </>
                      ) : (
                        p.summary
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={() => setExpandedId(expanded ? null : p.id)}
                      className="mt-2 text-[11px] text-violet-600 hover:underline"
                    >
                      {expanded ? 'Details ausblenden' : 'Details anzeigen'}
                    </button>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void handleApprove(p.id)}
                      className="p-2 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50"
                      title="Annehmen"
                    >
                      {busyApprove ? <LoadingSpinner /> : <Check className="w-4 h-4" />}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void handleReject(p.id)}
                      className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50"
                      title="Ablehnen"
                    >
                      {busyReject ? <LoadingSpinner /> : <X className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {expanded ? (
                  <div className="px-3 pb-3 border-t border-app-border bg-app-surface-muted">
                    <pre className="text-[10px] text-app-text-muted overflow-x-auto p-2 rounded mt-2 whitespace-pre-wrap">
                      {JSON.stringify({ payload: p.payload, evidence_refs: p.evidence_refs, instance_id: p.instance_id }, null, 2)}
                    </pre>
                    {p.proposal_type !== 'memory_fact' ? (
                      <p className="text-[10px] text-app-text-subtle mt-2">
                        Annehmen markiert den Vorschlag als freigegeben — automatisches Patchen von Skills/Prompt erfolgt noch nicht.
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
